'use client';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useTranslations } from 'next-intl';
import { type FC, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import InfiniteScroll from 'react-infinite-scroll-component';
import { TaskCard } from '@/app/[locale]/(app)/(workspace)/create/_components/TaskCard';
import { ChevronDown } from '@/app/components/icons/ChevronDown';
import { Loading } from '@/app/components/icons/Loading';
import { CustomTooltip } from '@/app/components/shared/CustomTooltip';
import { Divider } from '@/app/components/shared/Divider';
import { Empty } from '@/app/components/shared/Empty';
import { GlobalLoader } from '@/app/components/shared/GlobalLoader';
import ToTop from '@/app/components/shared/ToTop';
import { UpgradeBanner } from '@/app/components/shared/UpgradeBanner';
import { CheckboxFill } from '@/app/components/ui/checkbox-fill';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/app/components/ui/dropdown-menu';
import { SITE, TEXT_2_AUDIO_IMAGE, TYPE_KEY_MAP } from '@/app/constants';
import { PROCESSING_TASK_STATES, useFetch, useGlobalModal, useIsScreen, useProcessingCount } from '@/app/hooks';
import { useStore, useStoreApi } from '@/app/providers/store';
import type { CreationWithTask, TaskInfo } from '@/app/types';
import { cn, getAudioImageIndex, reportEvent } from '@/app/utils';
import { useLocale, usePathname, useRouter } from '@/i18n/routing';
import type { DisplayTaskState } from '../TaskCard/types';
import { tasksGetMyTaskHistory } from './apis';
import { CompactTaskHistoryItem } from './CompactTaskHistoryItem';
import { CompactTaskHistoryLoginGuide } from './CompactTaskHistoryLoginGuide';
import { TASK_PAGE_SIZE, VIDEO_TASK_TYPES } from './constants';
import type { ResolvedTaskHistoryTutorialEmptyState } from './utils';

interface TaskTypeFilterOption {
  key: TaskInfo['type'];
  label: string;
  queryTypes: TaskInfo['type'][];
}

const TTS_HISTORY_TASK_TYPES = ['tts'] as unknown as TaskInfo['type'][];

const FILTER_BUTTON_CLASS_NAME =
  'flex h-8 shrink-0 items-center gap-2 rounded-8 bg-system-white10 px-3 text-sm transition-colors cursor-pointer';
export const TaskHistory: FC<{
  isTemplates?: boolean;
  text2audio?: boolean;
  ttsOnly?: boolean;
  className?: string;
  align?: 'left' | 'center';
  showOngoingFilter?: boolean;
  showUpgradeBanner?: boolean;
  title?: string;
  showTaskTypeFilter?: boolean;
  processingFilterClassName?: string;
  scrollableTargetId?: string;
  allLoadedClassName?: string;
  defaultTaskTypeKeys?: TaskInfo['type'][];
  tutorialEmptyState?: ResolvedTaskHistoryTutorialEmptyState | null;
  compact?: boolean;
  onCompactCreationSelect?: (creation: CreationWithTask) => void;
  onCompactTaskSelect?: (task: TaskInfo, creation?: CreationWithTask, taskState?: DisplayTaskState) => void;
}> = ({
  text2audio = false,
  ttsOnly = false,
  className,
  align = 'left',
  showOngoingFilter = true,
  showUpgradeBanner = true,
  title,
  showTaskTypeFilter = true,
  processingFilterClassName,
  isTemplates = false,
  scrollableTargetId,
  allLoadedClassName,
  defaultTaskTypeKeys = [],
  tutorialEmptyState = null,
  compact = false,
  onCompactCreationSelect,
  onCompactTaskSelect,
}) => {
  const {
    taskList,
    cachedTaskListInitSource,
    cachedTaskListQueryKey,
    cachedTaskListNextPageToken,
    cachedTaskListUserId,
    initTaskList,
    updateCreation,
    appendTaskList,
    userInfo,
    isUserInfoInitialized,
    syncCreationDetailConfig,
  } = useStore((state) => ({
    taskList: state.taskList,
    cachedTaskListInitSource: state.taskListInitSource,
    cachedTaskListQueryKey: state.taskListQueryKey,
    cachedTaskListNextPageToken: state.taskListNextPageToken,
    cachedTaskListUserId: state.taskListUserId,
    initTaskList: state.initTaskList,
    updateCreation: state.updateCreation,
    appendTaskList: state.appendTaskList,
    userInfo: state.userInfo,
    isUserInfoInitialized: state.isUserInfoInitialized,
    syncCreationDetailConfig: state.syncCreationDetailConfig,
  }));
  const storeApi = useStoreApi();
  const tasks = useMemo(
    () =>
      taskList.map((task) => {
        if (task?.type !== 'text2audio' && task?.type !== 'tts') {
          return task;
        }
        return {
          ...task,
          creations:
            task.state === 'success'
              ? (task.creations ?? []).map((creation) => ({
                  ...creation,
                  audio_image: TEXT_2_AUDIO_IMAGE[getAudioImageIndex(creation.id, TEXT_2_AUDIO_IMAGE.length)],
                }))
              : task.creations,
        };
      }),
    [taskList],
  );
  const t = useTranslations();
  const pathname = usePathname();
  const router = useRouter();
  const { open: openLoginDialog } = useGlobalModal('loginDialog');
  const { isSmScreen } = useIsScreen();
  const locale = useLocale();

  const pageToken = useRef<string | undefined>(undefined);
  const [isInitialLoading, setIsInitialLoading] = useState(Boolean(userInfo));
  const [isError, setIsError] = useState(false);
  const [isProcessingTasksOnly, setIsProcessingTasksOnly] = useState<boolean>(false);
  const [selectedTaskTypeKeys, setSelectedTaskTypeKeys] = useState<TaskInfo['type'][] | null>(null);
  const [floatingContainer, setFloatingContainer] = useState<HTMLElement | null>(null);

  const pcScrollableTargetId = useId();

  const EMPTY_TEMPLATE_IMAGE =
    SITE === 'china'
      ? 'https://image01-aliyun.vidu.zone/vidu/media-asset/templates-empty-zh-9a1ab1fe.webp'
      : locale === 'zh'
        ? 'https://image01.cf.vidu.studio/vidu/media-asset/templates-empty-zh-9a1ab1fe.webp'
        : 'https://image01.cf.vidu.studio/vidu/media-asset/templates-empty-en-df564106.webp';

  // 获取悬浮按钮容器
  useEffect(() => {
    const container = document.getElementById('floating-buttons-container');
    setFloatingContainer(container);
  }, []);

  const [baseTaskTypes, currentTaskListInitSource] = useMemo(() => {
    if (text2audio) {
      return [VIDEO_TASK_TYPES.text2audio, 'text2audio' as const];
    }
    if (ttsOnly) {
      return [TTS_HISTORY_TASK_TYPES, 'tts' as const];
    }
    if (isTemplates) {
      return [VIDEO_TASK_TYPES.templatePage, 'template' as const];
    }
    return [VIDEO_TASK_TYPES.toolPage, 'create' as const];
  }, [isTemplates, text2audio, ttsOnly]);

  const taskTypeOptions = useMemo<TaskTypeFilterOption[]>(() => {
    const options: TaskTypeFilterOption[] = [
      {
        key: 'character2video',
        label: t(`create.${TYPE_KEY_MAP.character2video}`),
        queryTypes: ['character2video'],
      },
      {
        key: 'img2video',
        label: t(`create.${TYPE_KEY_MAP.img2video}`),
        queryTypes: ['img2video', 'headtailimg2video', 'multi_frame'],
      },
      {
        key: 'text2video',
        label: t(`create.${TYPE_KEY_MAP.text2video}`),
        queryTypes: ['text2video'],
      },
      {
        key: 'reference2image',
        label: t(`create.${TYPE_KEY_MAP.reference2image}`),
        queryTypes: ['reference2image'],
      },
      {
        key: 'text2image',
        label: t(`create.${TYPE_KEY_MAP.text2image}`),
        queryTypes: ['text2image'],
      },
      {
        key: 'image_edit',
        label: t('task.action.imageEdit'),
        queryTypes: ['image_edit'],
      },
      {
        key: 'video_edit',
        label: t('task.action.videoEdit'),
        queryTypes: ['video_edit'],
      },
      {
        key: 'scene',
        label: t(`create.${TYPE_KEY_MAP.scene}`),
        queryTypes: ['scene'],
      },
      {
        key: 'text2audio',
        label: t(`create.${TYPE_KEY_MAP.text2audio}`),
        queryTypes: ['text2audio'],
      },
      {
        key: 'upscale',
        label: t(`create.${TYPE_KEY_MAP.upscale}`),
        queryTypes: ['upscale'],
      },
      {
        key: 'extend',
        label: t(`create.${TYPE_KEY_MAP.video2extend}`),
        queryTypes: ['extend', 'video2extend', 'video2extail'],
      },
      {
        key: 'face_swap',
        label: t(`create.${TYPE_KEY_MAP.face_swap}`),
        queryTypes: ['face_swap'],
      },
      {
        key: 'upscale_image',
        label: t(`create.${TYPE_KEY_MAP.upscale_image}`),
        queryTypes: ['upscale_image'],
      },
      {
        key: 'one_click',
        label: t(`create.${TYPE_KEY_MAP.one_click}`),
        queryTypes: ['one_click'],
      },
      {
        key: 'music_video',
        label: t(`create.${TYPE_KEY_MAP.music_video}`),
        queryTypes: ['music_video'],
      },
      {
        key: 'lip_sync',
        label: t(`create.${TYPE_KEY_MAP.lip_sync}`),
        queryTypes: ['lip_sync'],
      },
      {
        key: 'offline_avatar',
        label: t(`create.${TYPE_KEY_MAP.offline_avatar}`),
        queryTypes: ['offline_avatar'],
      },
      {
        key: 'remake',
        label: t(`create.${TYPE_KEY_MAP.remake}`),
        queryTypes: ['remake'],
      },
      {
        key: 'short_story',
        label: t(`create.${TYPE_KEY_MAP.short_story}`),
        queryTypes: ['short_story'],
      },
      {
        key: 'style_transfer',
        label: t(`create.${TYPE_KEY_MAP.style_transfer}`),
        queryTypes: ['style_transfer'],
      },
      {
        key: 'motion_control',
        label: t(`create.${TYPE_KEY_MAP.motion_control}`),
        queryTypes: ['motion_control'],
      },
    ];

    return options.filter((option) => option.queryTypes.some((type) => baseTaskTypes.includes(type)));
  }, [baseTaskTypes, t]);

  const effectiveDefaultTaskTypeKeys = useMemo(
    () => (text2audio || isTemplates || ttsOnly ? [] : defaultTaskTypeKeys),
    [defaultTaskTypeKeys, isTemplates, text2audio, ttsOnly],
  );
  const activeTaskTypeKeys = selectedTaskTypeKeys ?? effectiveDefaultTaskTypeKeys;

  const effectiveTaskTypes = useMemo(() => {
    if (activeTaskTypeKeys.length === 0) {
      return baseTaskTypes;
    }

    return Array.from(
      new Set(
        taskTypeOptions
          .filter((option) => activeTaskTypeKeys.includes(option.key))
          .flatMap((option) => option.queryTypes)
          .filter((type) => baseTaskTypes.includes(type)),
      ),
    );
  }, [activeTaskTypeKeys, baseTaskTypes, taskTypeOptions]);

  const selectedTaskTypeLabels = useMemo(
    () => taskTypeOptions.filter((option) => activeTaskTypeKeys.includes(option.key)).map((option) => option.label),
    [activeTaskTypeKeys, taskTypeOptions],
  );

  const taskTypeButtonLabel = selectedTaskTypeLabels.length
    ? selectedTaskTypeLabels.join(', ')
    : t('creations.filters.labels.taskType');

  const renderTutorialEmptyState = () => {
    if (!shouldShowTutorialEmptyState || !tutorialEmptyState) {
      return null;
    }

    if (tutorialEmptyState.type === 'steps') {
      return (
        <div className="w-full min-w-0 max-w-[792px] sm:max-w-full">
          <div className="mb-10 px-4 text-center font-semibold text-[32px] text-system-white leading-[1.4] sm:mb-5 sm:px-0 sm:text-xl">
            {t(tutorialEmptyState.messageKey)}
          </div>
          <div className="grid grid-cols-3 gap-6 sm:grid-cols-1 sm:gap-4">
            {tutorialEmptyState.steps.map((step) => (
              <div key={step.titleKey} className="flex min-w-0 flex-col gap-4 text-left sm:gap-3 sm:p-3">
                <div className="aspect-[4/3] overflow-hidden rounded-[16px] sm:aspect-[240/180]">
                  <img
                    src={step.imageSrc}
                    alt="guide"
                    className="size-full rounded-[20px] bg-system-white04 object-cover"
                  />
                </div>
                <div className="flex min-w-0 flex-col gap-2">
                  <div className="text-sm text-system-white leading-[1.4]">{t(step.titleKey)}</div>
                  <div className="text-system-white64 text-xs leading-[1.4]">{t(step.descriptionKey)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }

    if (tutorialEmptyState.type === 'video') {
      return (
        <div className="w-full min-w-0 max-w-[792px] sm:max-w-full">
          <div className="mb-4 px-4 font-semibold text-lg text-system-white sm:px-0 sm:text-base">
            {t(tutorialEmptyState.messageKey)}
          </div>
          <video
            src={tutorialEmptyState.media.src}
            poster={tutorialEmptyState.media.posterSrc}
            className="aspect-video w-full rounded-[20px] object-cover sm:rounded-[16px]"
            autoPlay
            muted
            loop
            playsInline
            controls
          />
        </div>
      );
    }

    return (
      <div className="w-full min-w-0 max-w-[792px] sm:max-w-full">
        <div className="mb-4 px-4 font-semibold text-lg text-system-white sm:px-0 sm:text-base">
          {t(tutorialEmptyState.messageKey)}
        </div>
        <img
          src={tutorialEmptyState.media.src}
          className="w-full rounded-[20px] object-cover sm:rounded-[16px]"
          alt="guide"
        />
      </div>
    );
  };

  const { processingTaskCount } = useProcessingCount(effectiveTaskTypes);

  const currentTaskListQueryKey = useMemo(
    () =>
      JSON.stringify({
        sceneMode: isTemplates ? 'all' : 'none',
        states: isProcessingTasksOnly ? PROCESSING_TASK_STATES : [],
        types: effectiveTaskTypes,
        source: currentTaskListInitSource,
      }),
    [currentTaskListInitSource, effectiveTaskTypes, isProcessingTasksOnly, isTemplates],
  );

  // 首次进入页面，获取任务列表
  const {
    loading,
    run: fetchTaskList,
    refresh,
  } = useFetch(
    (isLoadMore: boolean) =>
      tasksGetMyTaskHistory({
        query: {
          'pager.page_token': isLoadMore ? pageToken.current : undefined,
          'pager.pagesz': TASK_PAGE_SIZE,
          scene_mode: isTemplates ? 'all' : 'none',
          states: isProcessingTasksOnly ? PROCESSING_TASK_STATES : [],
          types: effectiveTaskTypes,
        },
      }),
    {
      manual: true,
      onSuccess: ({ data, error }, [isLoadMore]) => {
        setIsInitialLoading(false);
        if (error) {
          // 仅在首次加载出错时设置全局错误，避免加载更多时因为单个请求失败导致整个列表被错误页覆盖
          if (!isLoadMore) {
            setIsError(true);
          }
          return;
        }

        setIsError(false);

        if (data) {
          if (!isLoadMore) {
            initTaskList(data.tasks, currentTaskListInitSource, {
              queryKey: currentTaskListQueryKey,
              userId: userInfo?.id,
              nextPageToken: data.next_page_token,
            });
          } else {
            appendTaskList(data.tasks, data.next_page_token);
          }
          pageToken.current = data.next_page_token;
        }
      },
    },
  );

  const hasMore = !!pageToken.current;
  const shouldShowTutorialEmptyState =
    !isSmScreen && !!tutorialEmptyState && (!userInfo || (!loading && !isInitialLoading && !tasks.length));
  const detailHasMoreRef = useRef<boolean>(false);
  const detailNextPageTokenRef = useRef<string | undefined>(undefined);
  const detailLoadingMoreRef = useRef<boolean>(false);

  const mapTasksToThumbList = useCallback((historyTasks: typeof tasks) => {
    return historyTasks
      .filter((task) => task.state === 'success')
      .flatMap((task) =>
        (task.creations ?? []).map((c) => ({
          id: c.id,
          cover_uri:
            c.type === 'audio'
              ? TEXT_2_AUDIO_IMAGE[getAudioImageIndex(c.id, TEXT_2_AUDIO_IMAGE.length)].cover[SITE]
              : c.cover_uri,
          is_favor: c.is_favor,
          origin_task_type: c.origin_task_type,
        })),
      )
      .filter((item) => !!item.id);
  }, []);

  const getCreationSyncCallbacks = useCallback(
    () => ({
      handleCollect: ({ id, obj_type }: { id: string; obj_type: 'creation' }) => {
        if (obj_type !== 'creation') return;
        const target = storeApi
          .getState()
          .taskList.find((task) => task.creations?.some((creation) => creation.id === id));
        if (!target?.id) return;
        updateCreation(target.id, id, { is_favor: true });
      },
      handleUnCollect: ({ id, obj_type }: { id: string; obj_type: 'creation' }) => {
        if (obj_type !== 'creation') return;
        const target = storeApi
          .getState()
          .taskList.find((task) => task.creations?.some((creation) => creation.id === id));
        if (!target?.id) return;
        updateCreation(target.id, id, { is_favor: false });
      },
      handleVote: ({
        id,
        obj_type,
        voteType,
      }: {
        id: string;
        obj_type: 'creation';
        voteType?: CreationWithTask['vote'];
      }) => {
        if (obj_type !== 'creation' || !voteType) return;
        const target = storeApi
          .getState()
          .taskList.find((task) => task.creations?.some((creation) => creation.id === id));
        if (!target?.id) return;
        updateCreation(target.id, id, { vote: voteType });
      },
      handleUnVote: ({ id, obj_type }: { id: string; obj_type: 'creation' }) => {
        if (obj_type !== 'creation') return;
        const target = storeApi
          .getState()
          .taskList.find((task) => task.creations?.some((creation) => creation.id === id));
        if (!target?.id) return;
        updateCreation(target.id, id, { vote: 'unspecified' });
      },
    }),
    [storeApi, updateCreation],
  );

  const handleDetailLoadMore = useCallback(async () => {
    if (detailLoadingMoreRef.current || !detailHasMoreRef.current || !detailNextPageTokenRef.current) return;

    detailLoadingMoreRef.current = true;
    const { data, error } = await tasksGetMyTaskHistory({
      query: {
        'pager.page_token': detailNextPageTokenRef.current,
        'pager.pagesz': TASK_PAGE_SIZE,
        scene_mode: isTemplates ? 'all' : 'none',
        states: isProcessingTasksOnly ? PROCESSING_TASK_STATES : [],
        types: effectiveTaskTypes,
      },
    });
    detailLoadingMoreRef.current = false;

    if (error || !data) return;

    const nextItems = mapTasksToThumbList(data.tasks ?? []);
    detailNextPageTokenRef.current = data.next_page_token;
    detailHasMoreRef.current = !!data.next_page_token;

    const currentConfig = storeApi.getState().creationDetailConfig?.thumbScrollListConfig;
    const currentList = currentConfig?.list ?? [];
    const merged = [...currentList];
    const getListItemId = (item: (typeof currentList)[number]) => ('id' in item ? item.id : '');
    const existingIds = new Set(currentList.map(getListItemId).filter(Boolean));
    nextItems.forEach((item) => {
      if (existingIds.has(item.id)) return;
      existingIds.add(item.id);
      merged.push(item);
    });

    syncCreationDetailConfig({
      openInNewTab: false,
      thumbScrollListConfig: {
        list: merged,
        hasMore: detailHasMoreRef.current,
        onLoadMore: handleDetailLoadMore,
        onThumbnailClick: (id: string) => {
          router.push(`/creation-detail/${id}`);
        },
      },
      ...getCreationSyncCallbacks(),
      onClose: () => router.back(),
    });
  }, [
    effectiveTaskTypes,
    isTemplates,
    isProcessingTasksOnly,
    mapTasksToThumbList,
    storeApi,
    syncCreationDetailConfig,
    router,
    getCreationSyncCallbacks,
  ]);

  const pcScrollableTargetRef = useRef<HTMLDivElement | null>(null);
  const setPcScrollableTargetRef = useCallback((node: HTMLDivElement | null) => {
    pcScrollableTargetRef.current = node;
    if (!node) return;
    // 解决 InfiniteScroll 组件库在PC端的样式问题（会偶尔出现一个滚动条）
    const scrollWrapper = node.getElementsByClassName('infinite-scroll-component');
    if (scrollWrapper.length > 0) {
      const scrollDiv: HTMLDivElement = scrollWrapper[0] as HTMLDivElement;
      scrollDiv.style.overflow = 'visible';
    } else {
      console.warn('Element with class "infinite-scroll-component" not found');
    }
  }, []);

  // 保存上一次 tasks[0] 的 id，便于比较是否有新项插入
  const prevFirstTaskIdRef = useRef<string | undefined>(tasks[0]?.id);

  // 获取实际的滚动容器元素
  const getScrollElement = useCallback(() => {
    // Mobile 端使用外部传入的 scrollableTargetId
    if (isSmScreen && scrollableTargetId) {
      return document.getElementById(scrollableTargetId);
    }
    // PC 端使用内部的 pcScrollableTargetId
    return pcScrollableTargetRef.current;
  }, [isSmScreen, scrollableTargetId]);

  const virtualizer = useVirtualizer({
    count: tasks.length,
    getScrollElement,
    estimateSize: () => (compact ? 124 : isSmScreen ? 400 : 600),
    overscan: isSmScreen ? 5 : 2,
    getItemKey: (index) => tasks[index]?.id ?? index,
  });

  // 仅在“打开详情”瞬间同步一次快照给 creation-detail，避免弹窗内加载反向驱动外层列表
  const syncCreationDetailSnapshot = useCallback(() => {
    const snapshotList = mapTasksToThumbList(tasks);
    detailNextPageTokenRef.current = pageToken.current || undefined;
    detailHasMoreRef.current = !!detailNextPageTokenRef.current;
    syncCreationDetailConfig({
      openInNewTab: false,
      thumbScrollListConfig: {
        list: snapshotList,
        hasMore: detailHasMoreRef.current,
        onLoadMore: handleDetailLoadMore,
        onThumbnailClick: (id: string) => {
          router.push(`/creation-detail/${id}`);
        },
      },
      ...getCreationSyncCallbacks(),
      onClose: () => router.back(),
    });
  }, [mapTasksToThumbList, tasks, handleDetailLoadMore, router, syncCreationDetailConfig, getCreationSyncCallbacks]);

  // 初始化获取任务列表，以及当筛选条件变化时重新获取
  useEffect(() => {
    if (!isUserInfoInitialized) {
      setIsInitialLoading(true);
      return;
    }

    if (!userInfo) {
      setIsInitialLoading(false);
      setIsError(false);
      initTaskList([], currentTaskListInitSource, {
        queryKey: currentTaskListQueryKey,
        nextPageToken: undefined,
      });
      pageToken.current = undefined;
      return;
    }

    const canReuseTaskList =
      cachedTaskListInitSource === currentTaskListInitSource &&
      cachedTaskListQueryKey === currentTaskListQueryKey &&
      cachedTaskListUserId === userInfo.id;

    if (canReuseTaskList) {
      setIsInitialLoading(false);
      setIsError(false);
      pageToken.current = cachedTaskListNextPageToken;
      return;
    }

    setIsInitialLoading(true);
    fetchTaskList(false);
  }, [
    cachedTaskListInitSource,
    cachedTaskListNextPageToken,
    cachedTaskListQueryKey,
    cachedTaskListUserId,
    currentTaskListInitSource,
    currentTaskListQueryKey,
    fetchTaskList,
    initTaskList,
    isUserInfoInitialized,
    userInfo,
  ]);

  useEffect(() => {
    setSelectedTaskTypeKeys(null);
  }, [effectiveDefaultTaskTypeKeys]);

  // 如果筛选了处理中的任务，并且处理中任务数发生变化时，刷新
  useEffect(() => {
    if (isProcessingTasksOnly) {
      refresh();
    }
  }, [processingTaskCount]);

  useEffect(() => {
    if (isSmScreen) return;

    // 判断是否有新任务插入到列表顶部（原本的第一项恰好变成了现在的第二项）
    const isNewTaskInsertedAtTop = tasks.length > 1 && prevFirstTaskIdRef.current === tasks[1]?.id;

    // 如果之前的滚动高度不为 0，调整滚动位置，保持当前可见任务不变
    if (isNewTaskInsertedAtTop) {
      const scrollElement = getScrollElement();
      if (scrollElement?.scrollTop) {
        const newItemHeight = virtualizer.getVirtualItems()[0]?.size ?? 0;
        scrollElement.scrollTop += newItemHeight;
      }
    }
    prevFirstTaskIdRef.current = tasks[0]?.id;
  }, [tasks.length, virtualizer, getScrollElement]);

  const handleOpenDetail = useCallback(
    (creation: CreationWithTask) => {
      if (!creation?.id) return;
      syncCreationDetailSnapshot();
      router.push(`/creation-detail/${creation.id}`);
    },
    [router, syncCreationDetailSnapshot],
  );

  // 横幅贴在卡片顶沿，因此挂在滚动容器外层，不受列表左右内边距影响
  const renderUpgradeBanner = showUpgradeBanner && <UpgradeBanner />;

  const renderProcessingTaskFilter = showOngoingFilter && (
    <div
      className={cn(
        'sticky top-0 left-0 z-10 w-full bg-[#1A1C20]',
        !processingFilterClassName && 'sm:static sm:bg-transparent',
        processingFilterClassName,
      )}
    >
      <div className="mt-2 flex w-full flex-wrap items-center gap-2 py-2 pb-4">
        {title ? <h2 className="mr-auto font-semibold text-base text-system-white">{title}</h2> : null}
        <div
          className={cn(FILTER_BUTTON_CLASS_NAME, isProcessingTasksOnly && 'bg-system-white24')}
          onClick={() => {
            setIsProcessingTasksOnly(!isProcessingTasksOnly);
          }}
        >
          <CheckboxFill checked={isProcessingTasksOnly} className="text-base" />
          <span>{t('task.filterProcessing')}</span>
          {processingTaskCount > 0 && (
            <span className="inline-flex h-[18px] min-w-[18px] shrink-0 items-center justify-center rounded-full bg-system-blue02 px-1 text-center text-system-white text-xs leading-none">
              {processingTaskCount}
            </span>
          )}
        </div>

        {showTaskTypeFilter && taskTypeOptions.length > 1 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className={cn(
                  FILTER_BUTTON_CLASS_NAME,
                  'max-w-[128px]',
                  activeTaskTypeKeys.length > 0 && 'bg-system-white24',
                )}
              >
                <CustomTooltip content={taskTypeButtonLabel} showOnlyOnOverflow maxLines={1}>
                  <span>{taskTypeButtonLabel}</span>
                </CustomTooltip>
                <ChevronDown className="shrink-0 text-base" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="start"
              className="max-h-[calc(100vh-300px)] space-y-1 overflow-y-auto border border-system-white12 bg-[#1A1A1A]"
            >
              <DropdownMenuCheckboxItem
                checked={activeTaskTypeKeys.length === 0}
                onSelect={(e) => e.preventDefault()}
                onClick={() => {
                  setSelectedTaskTypeKeys([]);
                }}
              >
                {t('creations.filters.all')}
              </DropdownMenuCheckboxItem>
              {taskTypeOptions.map((option) => (
                <DropdownMenuCheckboxItem
                  key={option.key}
                  checked={activeTaskTypeKeys.includes(option.key)}
                  onSelect={(e) => e.preventDefault()}
                  onClick={() => {
                    setSelectedTaskTypeKeys(
                      activeTaskTypeKeys.includes(option.key)
                        ? activeTaskTypeKeys.filter((key) => key !== option.key)
                        : [...activeTaskTypeKeys, option.key],
                    );
                  }}
                >
                  {option.label}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </div>
  );

  const scrollToTop = () => {
    const scrollElement = getScrollElement();
    if (scrollElement) {
      scrollElement.scrollTop = 0;
      // 如果是 document.documentElement，也同步滚动
      if (scrollElement !== document.documentElement) {
        document.documentElement.scrollTop = 0;
      }
    }
  };

  // 渲染 ToTop 组件，只有在任务列表不为空时才显示
  const renderToTop = () => {
    if (compact || !tasks.length || !floatingContainer) return null;

    return createPortal(
      <ToTop scrollDiv={getScrollElement()} topText={isSmScreen ? '' : t('task.toTop')} />,
      floatingContainer,
    );
  };

  //只有在初始加载的时候展示loading
  if (!isUserInfoInitialized) {
    return (
      <div
        className={cn(
          'relative flex h-full w-full flex-1 flex-col items-center rounded-12 bg-[#1A1C20] pr-6 pl-4 sm:h-auto sm:min-h-[152px] sm:bg-transparent sm:pr-0 sm:pl-0',
          className,
        )}
      >
        {renderProcessingTaskFilter}
        <GlobalLoader className="top-1/2" />
      </div>
    );
  }

  if (!userInfo) {
    if (compact) {
      return <CompactTaskHistoryLoginGuide label={t('task.loginToViewHistory')} className={className} />;
    }

    if (shouldShowTutorialEmptyState) {
      return (
        <div
          className={cn(
            'relative flex h-full w-full flex-1 flex-col items-center justify-center rounded-12 bg-[#1A1C20] px-4 py-6 text-center sm:hidden sm:min-h-[152px] sm:items-stretch sm:justify-start sm:bg-transparent sm:px-0',
            className,
          )}
        >
          {renderTutorialEmptyState()}
        </div>
      );
    }

    return (
      <div
        className={cn(
          'relative flex h-full w-full flex-1 flex-col items-center justify-center rounded-12 bg-[#1A1C20] px-6 text-center sm:hidden sm:min-h-[152px] sm:bg-transparent sm:px-0',
          className,
        )}
      >
        <div className="font-semibold text-lg text-system-white">{t('loginGuideDialog.title')}</div>
        <div className="mt-2 max-w-[280px] text-sm text-system-white64">{t('loginGuideDialog.description')}</div>
        <button
          type="button"
          className="mt-5 h-10 rounded-full bg-ShengshuButton px-6 font-semibold text-black hover:bg-ShengshuButtonHover"
          onClick={() => {
            openLoginDialog();
            reportEvent('login_pop_exposure', {
              refer_element: 'task-history-login-guide',
              refer_page: pathname,
            });
          }}
        >
          {t('header.navLogin')}
        </button>
      </div>
    );
  }

  if ((loading && !pageToken.current) || isInitialLoading || isError || !tasks.length) {
    return (
      <div
        className={cn(
          'relative flex h-full w-full flex-1 flex-col rounded-12 bg-[#1A1C20] sm:h-auto sm:min-h-[152px] sm:bg-transparent',
          className,
        )}
      >
        {renderUpgradeBanner}
        <div className="flex min-h-0 w-full flex-1 flex-col items-center pr-6 pl-4 sm:pr-0 sm:pl-0">
          {loading || isInitialLoading ? (
            <>
              {renderProcessingTaskFilter}
              <GlobalLoader className="top-1/2" />
            </>
          ) : isError ? (
            <span className="w-full text-center text-system-white64">{t('loadError')}</span>
          ) : (
            <>
              {renderProcessingTaskFilter}
              {isTemplates ? (
                <div className="flex h-full flex-col items-center justify-center pb-12">
                  <img src={EMPTY_TEMPLATE_IMAGE} alt="" className="w-[658px]" />
                </div>
              ) : shouldShowTutorialEmptyState && !isProcessingTasksOnly ? (
                <div className="flex h-full w-full min-w-0 flex-col items-center justify-center py-6 text-center sm:h-auto sm:items-stretch sm:py-4">
                  {renderTutorialEmptyState()}
                </div>
              ) : compact ? (
                <span className="flex h-full items-center justify-center" aria-hidden="true" />
              ) : (
                <Empty emptyText={isProcessingTasksOnly ? t('task.processingEmpty') : t('task.empty')} />
              )}
            </>
          )}
        </div>
      </div>
    );
  }

  const virtualItems = virtualizer.getVirtualItems();

  return (
    <>
      <div
        id="taskHistoryContainer"
        className={cn(
          'relative flex h-full flex-1 flex-col rounded-12 bg-[#1A1C20] sm:h-fit sm:w-full sm:bg-transparent',
          className,
        )}
      >
        {renderUpgradeBanner}
        <div
          id={pcScrollableTargetId}
          ref={setPcScrollableTargetRef}
          className={cn(
            'min-h-0 flex-1 overflow-y-auto sm:overflow-y-visible sm:pr-0 sm:pl-0',
            align === 'left' && !compact && 'pr-6 pl-4',
          )}
        >
          {renderProcessingTaskFilter}
          <InfiniteScroll
            dataLength={tasks.length}
            next={() => fetchTaskList(true)}
            hasMore={hasMore && !loading && !isError}
            loader={null}
            scrollThreshold={0.95}
            scrollableTarget={isSmScreen ? scrollableTargetId : pcScrollableTargetId}
          >
            <div
              className="relative w-full"
              style={{
                height: virtualizer.getTotalSize(),
              }}
            >
              <div
                className={cn(
                  'absolute top-0 w-full space-y-9 sm:space-y-4',
                  align === 'left' && 'left-0 max-w-[1648px]',
                  align === 'center' && 'left-1/2 max-w-[840px]',
                )}
                style={{
                  transform:
                    align === 'center'
                      ? `translateY(${virtualItems[0]?.start ?? 0}px) translateX(-50%)`
                      : `translateY(${virtualItems[0]?.start ?? 0}px)`,
                }}
              >
                {virtualItems.map((virtualRow) => {
                  const task = tasks[virtualRow.index];
                  return (
                    <div key={virtualRow.key} data-index={virtualRow.index} ref={virtualizer.measureElement}>
                      {compact ? (
                        <CompactTaskHistoryItem
                          task={task}
                          onOpenDetail={handleOpenDetail}
                          onCompactCreationSelect={onCompactCreationSelect}
                          onCompactTaskSelect={onCompactTaskSelect}
                        />
                      ) : (
                        <TaskCard
                          isTemplates={isTemplates}
                          key={task.id}
                          task={task}
                          onOpenDetail={handleOpenDetail}
                          scrollToTop={isSmScreen ? scrollToTop : undefined}
                        />
                      )}
                    </div>
                  );
                })}
                {loading && (
                  <div className="flex items-center justify-center py-4">
                    <Loading className="animate-spin" />
                  </div>
                )}
                {!hasMore && tasks.length > 0 && !loading && !isError && (
                  <Divider
                    className={cn(
                      'w-full select-none gap-1 whitespace-nowrap pb-[72px] text-system-blue02 group-hover:text-system-blue02 sm:pb-9',
                      compact && 'hidden',
                      allLoadedClassName,
                    )}
                  >
                    <span>{t('task.allLoaded')}</span>
                  </Divider>
                )}
              </div>
            </div>
          </InfiniteScroll>
        </div>
      </div>

      {/* 使用 Portal 将 ToTop 组件渲染到 layout 的悬浮按钮区域 */}
      {renderToTop()}
    </>
  );
};

export {
  type CompactTaskHistoryStateMessage,
  getCompactTaskHistoryStateMessage,
} from './compact-task-history-item';
